import { BodyText } from '@/components/BodyText';
import { HeaderText } from '@/components/HeaderText';
import { createUseStyles } from '@/services/createUseStyles';
import { useTenants } from '@/services/tenants/useTenants';
import { Alert, Box, Button } from '@mui/material';
import { BulkUploadData } from '../../types';
import { createConfirmationStepColumns } from '../columnDefinitions';
import { MetadataBulkDataGrid } from '../MetadataBulkDataGrid';

const useStyles = createUseStyles(theme => ({
  ConfirmationStep_root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  ConfirmationStep_content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    padding: '24px',
  },
  ConfirmationStep_summary: {
    marginBottom: '24px',
    padding: '16px',
    backgroundColor: theme.palette.background.default,
    borderRadius: '8px',
    flexShrink: 0,
  },
  ConfirmationStep_dataGridContainer: {
    flex: 1,
    overflow: 'hidden',
    marginBottom: '24px',
  },
  ConfirmationStep_actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    position: 'sticky',
    bottom: 0,
    flexShrink: 0,
  },
  ConfirmationStep_metadataCell: {
    maxWidth: '300px',
  },
  ConfirmationStep_metadataChips: {
    display: 'flex',
    gap: '4px',
    flexWrap: 'wrap',
  },
}));

export const ConfirmationStep = ({
  data,
  onNext,
  onBack,
}: {
  data?: BulkUploadData;
  onNext: () => void;
  onBack: () => void;
}) => {
  const c = useStyles();
  const { getPath } = useTenants();

  if (!data) {
    return <Alert severity='error'>No data available. Please go back and upload a file.</Alert>;
  }

  const { parsedData, metadataFields, metadataFieldDefinitions, assetMatches } = data;
  const newFields = metadataFieldDefinitions.filter(field => field.isNew);

  const matchedAssets = assetMatches.filter(
    match =>
      (match.status === 'matched' && match.selectedAsset) || (match.status === 'asset_id_provided' && match.assetId)
  );

  const totalUpdates = matchedAssets.reduce((total, match) => {
    const row = parsedData.find(r => r.assetName === match.assetName);
    return total + (row ? Object.keys(row.metadata).length : 0);
  }, 0);

  const getAssetUrl = (assetId: string) => {
    return getPath(`asset/${assetId}`);
  };

  const columns = createConfirmationStepColumns(getAssetUrl, data);

  const rows = matchedAssets.map((match, index) => ({
    id: `match-${index}`,
    name: match.assetName,
    assetId: match.selectedAsset?.id || match.assetId,
    ...match,
  }));

  return (
    <Box className={c.ConfirmationStep_root}>
      <Box className={c.ConfirmationStep_content}>
        <HeaderText gutterBottom>Confirm Changes</HeaderText>
        <Box className={c.ConfirmationStep_summary}>
          <HeaderText gutterBottom>Update Summary</HeaderText>
          <BodyText>
            <strong>Assets to Update:</strong> {matchedAssets.length}
          </BodyText>
          <BodyText>
            <strong>Total Metadata Updates:</strong> {totalUpdates}
          </BodyText>
          <BodyText>
            <strong>Metadata Fields:</strong> {metadataFields.join(', ')}
          </BodyText>
          {newFields.length > 0 && (
            <BodyText color='warning.main'>
              <strong>New Fields to Create:</strong> {newFields.map(f => `${f.name} (${f.type})`).join(', ')}
            </BodyText>
          )}
        </Box>
        <Alert severity='info' sx={{ marginBottom: 2 }}>
          <BodyText>
            The following changes will be applied to your assets. Please review carefully before proceeding.
          </BodyText>
        </Alert>
        <HeaderText gutterBottom>Assets and Metadata Updates ({matchedAssets.length} assets)</HeaderText>
        <Box className={c.ConfirmationStep_dataGridContainer}>
          <MetadataBulkDataGrid columns={columns} rows={rows} rowCount={rows.length} />
        </Box>
        {parsedData.length > matchedAssets.length && (
          <Alert severity='warning' sx={{ marginTop: 2 }}>
            <BodyText>
              <strong>Note:</strong> {parsedData.length - matchedAssets.length} assets from your file will be skipped
              because no matching assets were found or selected.
            </BodyText>
          </Alert>
        )}
      </Box>
      <Box className={c.ConfirmationStep_actions}>
        <Button variant='outlined' onClick={onBack}>
          Back
        </Button>
        <Button variant='contained' color='primary' onClick={onNext} disabled={matchedAssets.length === 0}>
          Start Update Process
        </Button>
      </Box>
    </Box>
  );
};
